import * as THREE from 'three';
import { fbm2, mulberry32 } from '../../core/rng';
import type { BuiltLocation, LocationDef } from '../Location';
import { paintableIceMaterial, paintableSnowMaterial, registerPaintable } from '../paintable';
import { box, building, collider, disposeScene, skyDome, stars, stdMat } from '../props';
import { createTerrain } from '../terrain';

const HALF = 23;

/** Snowed-in rooftop garden at night: planters, string lights, a greenhouse
 * with paintable frosted glass, and the city glowing below the parapet. */
export const rooftopLocation: LocationDef = {
  id: 'rooftop',
  name: 'Rooftop Garden',
  description: 'A garden above the city lights. Paint the roof — or the greenhouse glass.',
  thumbnail: { skyTop: '#0b1322', skyBottom: '#2c3550', ground: '#e9edf5', accent: '#ffc87a' },

  build(ctx): BuiltLocation {
    const scene = new THREE.Scene();
    const colliders: THREE.Box3[] = [];
    const paintables: BuiltLocation['paintables'] = [];

    scene.fog = new THREE.FogExp2(0x131a2b, 0.004);
    scene.add(
      skyDome([
        [0, '#070b16'],
        [0.6, '#101a30'],
        [0.85, '#27304c'],
        [1, '#3d3a52'],
      ]),
    );
    scene.add(stars(600, 3));

    // City glow: cool sky, warm bounce from the streets far below.
    scene.add(new THREE.HemisphereLight(0x46567c, 0x9a7a56, 1.15));
    const moon = new THREE.DirectionalLight(0xa8bce0, 0.7);
    moon.position.set(-50, 80, -30);
    moon.castShadow = true;
    moon.shadow.camera.left = -30;
    moon.shadow.camera.right = 30;
    moon.shadow.camera.top = 30;
    moon.shadow.camera.bottom = -30;
    moon.shadow.bias = -0.0004;
    scene.add(moon);

    const height = (x: number, z: number) => fbm2(x * 0.09, z * 0.09, ctx.seed) * 0.07;
    const snow = paintableSnowMaterial({ grainScale: 700 });
    const terrain = createTerrain({
      size: HALF * 2,
      segments: 70,
      height,
      material: snow.material,
    });
    scene.add(terrain.mesh);
    registerPaintable(paintables, 'ground', terrain.mesh, snow.uniform, true);

    // Parapet walls.
    const parapetMat = stdMat(0x585d68, 0.9);
    const capMat = stdMat(0xe9eef6, 0.95);
    for (const [x, z, w, d] of [
      [0, -HALF, HALF * 2 + 0.6, 0.6],
      [0, HALF, HALF * 2 + 0.6, 0.6],
      [-HALF, 0, 0.6, HALF * 2 + 0.6],
      [HALF, 0, 0.6, HALF * 2 + 0.6],
    ] as const) {
      scene.add(box(w, 1.1, d, parapetMat, x, 0.55, z));
      scene.add(box(w, 0.1, d, capMat, x, 1.13, z));
      colliders.push(collider(x, z, w, d, -1, 1.2));
    }

    // Greenhouse with paintable frosted-glass roof panels (sloped surfaces).
    const gh = new THREE.Group();
    const frame = stdMat(0x3e4a52, 0.6, 0.4);
    const glassMat = new THREE.MeshStandardMaterial({
      color: 0xd6e8ef,
      roughness: 0.35,
      transparent: true,
      opacity: 0.32,
    });
    for (const [px, pz] of [
      [-2.6, -1.6],
      [2.6, -1.6],
      [-2.6, 1.6],
      [2.6, 1.6],
    ] as const) {
      gh.add(box(0.14, 2.3, 0.14, frame, px, 1.15, pz));
    }
    // Glass walls.
    const wallA = new THREE.Mesh(new THREE.PlaneGeometry(5.2, 2.3), glassMat);
    wallA.position.set(0, 1.15, -1.6);
    const wallB = wallA.clone();
    wallB.position.z = 1.6;
    const wallC = new THREE.Mesh(new THREE.PlaneGeometry(3.2, 2.3), glassMat);
    wallC.rotation.y = Math.PI / 2;
    wallC.position.set(-2.6, 1.15, 0);
    const wallD = wallC.clone();
    wallD.position.x = 2.6;
    gh.add(wallA, wallB, wallC, wallD);
    // Paintable frosted roof panels, tilted — a nice slope-projection showcase.
    const roofPanel = (id: string, zSign: number) => {
      const bound = paintableIceMaterial({ color: 0xdfeef4 });
      const panel = new THREE.Mesh(new THREE.PlaneGeometry(5.4, 1.95), bound.material);
      panel.geometry.rotateX(-Math.PI / 2 + zSign * 0.5);
      panel.position.set(0, 2.72, zSign * 0.86);
      panel.material.side = THREE.DoubleSide;
      gh.add(panel);
      registerPaintable(paintables, id, panel, bound.uniform, false);
    };
    roofPanel('greenhouse-a', -1);
    roofPanel('greenhouse-b', 1);
    const ghLight = new THREE.PointLight(0xbfe8c8, 20, 11, 2);
    ghLight.position.set(0, 1.6, 0);
    gh.add(ghLight);
    gh.position.set(-11, 0, -10);
    scene.add(gh);
    colliders.push(collider(-11, -10, 5.6, 3.6, 0, 3.4));

    // Planters with dwarf shrubs.
    const planterMat = stdMat(0x5e4a3a, 0.95);
    const shrubMat = stdMat(0x35543f, 0.95);
    const rand = mulberry32(ctx.seed + 17);
    for (const [x, z] of [
      [10, -12],
      [14, -4],
      [10, 4],
      [-6, 12],
      [4, 14],
      [-14, 6],
    ] as const) {
      scene.add(box(2.4, 0.7, 1.2, planterMat, x, 0.35, z, rand() * 3));
      const shrub = new THREE.Mesh(new THREE.SphereGeometry(0.55, 10, 8), shrubMat);
      shrub.position.set(x, 1.05, z);
      shrub.scale.y = 0.8;
      scene.add(shrub);
      const cap = new THREE.Mesh(
        new THREE.SphereGeometry(0.5, 10, 6, 0, Math.PI * 2, 0, 1.1),
        capMat,
      );
      cap.position.set(x, 1.2, z);
      scene.add(cap);
      colliders.push(collider(x, z, 2.5, 1.4, 0, 1.1));
    }

    // String lights between two poles.
    const poleMat = stdMat(0x2c3038, 0.6, 0.5);
    scene.add(box(0.12, 3.4, 0.12, poleMat, -8, 1.7, 8));
    scene.add(box(0.12, 3.4, 0.12, poleMat, 12, 1.7, -8));
    colliders.push(collider(-8, 8, 0.3, 0.3, 0, 3.4), collider(12, -8, 0.3, 0.3, 0, 3.4));
    const bulbMat = new THREE.MeshStandardMaterial({
      color: 0xffd9a0,
      emissive: 0xffb35c,
      emissiveIntensity: 2.4,
    });
    const bulbGeo = new THREE.SphereGeometry(0.07, 8, 6);
    const from = new THREE.Vector3(-8, 3.35, 8);
    const to = new THREE.Vector3(12, 3.35, -8);
    for (let i = 1; i < 14; i++) {
      const t = i / 14;
      const p = from.clone().lerp(to, t);
      p.y -= Math.sin(t * Math.PI) * 0.9; // sag
      const bulb = new THREE.Mesh(bulbGeo, bulbMat);
      bulb.position.copy(p);
      scene.add(bulb);
    }
    const stringGlow = new THREE.PointLight(0xffb35c, 55, 24, 2);
    stringGlow.position.set(2, 2.6, 0);
    scene.add(stringGlow);

    // Skyline below the parapet: towers ringing the roof.
    const skyRand = mulberry32(ctx.seed + 99);
    for (let i = 0; i < 18; i++) {
      const angle = (i / 18) * Math.PI * 2 + skyRand() * 0.25;
      const r = 60 + skyRand() * 55;
      const h = 22 + skyRand() * 26;
      const x = Math.cos(angle) * r;
      const z = Math.sin(angle) * r;
      scene.add(building(12 + skyRand() * 10, h, 12 + skyRand() * 10, x, z, -42, null, '#232b3c'));
    }

    return {
      scene,
      spawn: { pos: new THREE.Vector3(8, height(8, 8), 8), yaw: 0.82 },
      paintables,
      colliders,
      groundHeight: height,
      groundSurfaceIdAt: () => 'ground',
      bounds: { minX: -HALF + 1, maxX: HALF - 1, minZ: -HALF + 1, maxZ: HALF - 1 },
      baseFogDensity: 0.004,
      dispose() {
        disposeScene(scene);
      },
    };
  },
};
